import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { games as gamesApi, formatApiError } from "../lib/api";
import LiveBoard from "../components/LiveBoard";
import { Radio, Loader2, Users } from "lucide-react";

const SS_KEY = (code) => `canales.game_join.${code}`;

export default function GameJoinPage() {
  const { code: codeParam } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [code, setCode] = useState((codeParam || "").toUpperCase());
  const [entered, setEntered] = useState(!!codeParam);
  const [room, setRoom] = useState(null);
  const [team, setTeam] = useState(null);
  const [token, setToken] = useState(null);
  const [joining, setJoining] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const pollRef = useRef(null);

  // restore team/token for this code
  useEffect(() => {
    if (!code) return;
    try {
      const saved = JSON.parse(sessionStorage.getItem(SS_KEY(code)) || "null");
      if (saved?.team && saved?.token) {
        setTeam(saved.team);
        setToken(saved.token);
      }
    } catch { /* ignore */ }
  }, [code]);

  const fetchRoom = useCallback(async () => {
    if (!code || !entered) return;
    try {
      const r = await gamesApi.rooms.get(code);
      setRoom(r);
      setNotFound(false);
    } catch {
      setNotFound(true);
    }
  }, [code, entered]);

  useEffect(() => {
    if (!entered) return;
    fetchRoom();
    pollRef.current = setInterval(fetchRoom, 1500);
    return () => clearInterval(pollRef.current);
  }, [entered, fetchRoom]);

  const enterCode = async () => {
    const c = code.trim().toUpperCase();
    if (c.length < 3) return toast.error(t("games.live.enter_code"));
    setCode(c);
    setLoading(true);
    try {
      await gamesApi.rooms.get(c);
      setEntered(true);
      navigate(`/join/${c}`, { replace: true });
    } catch {
      toast.error(t("games.live.room_not_found"));
    } finally {
      setLoading(false);
    }
  };

  const joinTeam = async (slot) => {
    setJoining(true);
    try {
      const res = await gamesApi.rooms.join(code, { team: slot });
      setTeam(res.team);
      setToken(res.token);
      sessionStorage.setItem(SS_KEY(code), JSON.stringify({ team: res.team, token: res.token }));
      setRoom(res.room);
    } catch (e) {
      toast.error(formatApiError(e, t("games.save_error")));
    } finally {
      setJoining(false);
    }
  };

  const doPick = async (optionKey) => {
    try {
      const r = await gamesApi.rooms.pick(code, { team, token, option_key: optionKey });
      setRoom(r);
    } catch (e) {
      toast.error(formatApiError(e, t("games.live.pick_error")));
      fetchRoom();
    }
  };

  // ---------- ENTER CODE ----------
  if (!entered) {
    return (
      <div className="min-h-screen bg-[#0A1329] text-white grid place-items-center px-6" data-testid="game-join-code">
        <div className="w-full max-w-sm text-center">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#FDE68A] flex items-center justify-center gap-2">
            <Radio size={14} /> Canales Dijeron
          </div>
          <h1 className="font-display text-3xl font-extrabold mt-2">{t("games.live.join_title")}</h1>
          <p className="text-slate-400 text-sm mt-1">{t("games.live.join_hint")}</p>
          <input
            data-testid="join-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && enterCode()}
            maxLength={6}
            placeholder="ABCD"
            className="mt-6 w-full text-center font-display text-5xl font-black tracking-[0.3em] bg-[#0F1E3D] border-2 border-[#1E3A8A] rounded-2xl py-4 focus:outline-none focus:ring-2 focus:ring-[#F59E0B] uppercase"
          />
          <button onClick={enterCode} disabled={loading} data-testid="join-enter-btn"
            className="mt-5 w-full bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-[#0A1329] font-extrabold text-lg py-3.5 rounded-2xl hover:brightness-110 transition-[filter] disabled:opacity-40 inline-flex items-center justify-center gap-2">
            {loading ? <Loader2 size={18} className="animate-spin" /> : null}
            {t("games.live.enter")}
          </button>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#0A1329] text-white grid place-items-center px-6 text-center" data-testid="join-not-found">
        <div>
          <p className="text-lg font-semibold">{t("games.live.room_not_found")}</p>
          <button onClick={() => { setEntered(false); setNotFound(false); }} className="mt-3 text-[#F59E0B] underline text-sm">
            {t("games.live.try_another")}
          </button>
        </div>
      </div>
    );
  }

  if (!room) {
    return <div className="min-h-screen bg-[#0A1329] text-white grid place-items-center">{t("common.loading")}</div>;
  }

  // ---------- CHOOSE TEAM ----------
  if (!team || !token) {
    return (
      <div className="min-h-screen bg-[#0A1329] text-white grid place-items-center px-6" data-testid="join-choose-team">
        <div className="w-full max-w-md text-center">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#FDE68A]">{room.game_title}</div>
          <h1 className="font-display text-3xl font-extrabold mt-2">{t("games.live.pick_your_store")}</h1>
          <div className="mt-6 grid grid-cols-1 gap-3">
            <button onClick={() => joinTeam("A")} disabled={joining} data-testid="join-team-a"
              className="rounded-2xl border-2 border-[#1E3A8A] bg-[#0F1E3D] px-5 py-5 hover:border-[#3B82F6] transition-colors disabled:opacity-50">
              <div className="flex items-center justify-center gap-2 font-bold text-lg text-[#3B82F6]"><Users size={18} /> {room.team_a_name}</div>
              <div className="text-xs text-slate-400 mt-1">{room.team_a_joined ? t("games.live.slot_taken_hint") : t("games.live.tap_to_join")}</div>
            </button>
            <button onClick={() => joinTeam("B")} disabled={joining} data-testid="join-team-b"
              className="rounded-2xl border-2 border-[#6B3E2E] bg-[#0F1E3D] px-5 py-5 hover:border-[#F59E0B] transition-colors disabled:opacity-50">
              <div className="flex items-center justify-center gap-2 font-bold text-lg text-[#F59E0B]"><Users size={18} /> {room.team_b_name}</div>
              <div className="text-xs text-slate-400 mt-1">{room.team_b_joined ? t("games.live.slot_taken_hint") : t("games.live.tap_to_join")}</div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- WAITING LOBBY ----------
  if (room.status === "lobby") {
    const myName = team === "A" ? room.team_a_name : room.team_b_name;
    return (
      <div className="min-h-screen bg-[#0A1329] text-white grid place-items-center px-6 text-center" data-testid="join-waiting">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#FDE68A]">{t("games.live.you_are")}</div>
          <h1 className={`font-display text-3xl font-extrabold mt-1 ${team === "A" ? "text-[#3B82F6]" : "text-[#F59E0B]"}`}>{myName}</h1>
          <div className="mt-6 inline-flex items-center gap-2 text-slate-300">
            <Loader2 size={18} className="animate-spin" /> {t("games.live.waiting_host")}
          </div>
        </div>
      </div>
    );
  }

  // ---------- PLAYING / FINISHED ----------
  return <LiveBoard room={room} role={team} onPick={doPick} soundOn />;
}
